export const meta = {
  id: '26-future',
  order: 26,
  checkFocus: { tense: ['future'] },   // what the end-of-lesson check tests first
  titleFi: 'Futuuri',
  titleEn: 'Future',
  duolingoUnit: 'duo-31-future',
  // + Sanapaja's own words this lesson first needs (docs/COURSE.md "Own words in the lessons"; waves/wave-NN/lessons.json)
  lemmaIds: ['tubī', 'nektogon', 'sindigon', 'ours:mīkeliāzma', 'ours:zonkiapos', 'ours:ōghriapos', 'ours:perzalbar', 'ours:jehītsos', 'ours:perziapos', 'ours:tojalion', 'ours:pirtiaptys', 'ours:selion', 'ours:umbarenka', 'ours:groziorlion', 'ours:ojēbion', 'ours:nopāzmīha', 'ours:rholilla', 'ours:epnon', 'ours:ajorrāeltir', 'ours:ozurnēptys', 'ours:kalendari', 'ours:hūro-tubis', 'ours:vagro-tubis', 'ours:hopio-tubis', 'ours:balerio-tubis', 'ours:merakso-tubis', 'ours:iōdio-tubis', 'ours:vēzo-tubis', 'ours:masso-bantis', 'ours:sīkudio-mōris', 'ours:dūmio', 'ours:kostymi', 'ours:biriapos', 'ours:vampiri', 'ours:kȳvatir', 'ours:zombi'],
  exampleIds: [
    'hotd-s3e03-011',
    'ex-c41ad9ec5a12',
    'ex-2f08a2eb9b06',
    'ex-26431af6f541',
    'ex-b9db50ee2817',
    'hotd-s1e01-021',
    'ex-0cb563e580b2',
    'ex-d5afeb2c0e99',
    'got-s5e03-011',
    'ex-e4d918a6dd2b',
  ],
  sources: [
    'https://wiki.languageinvention.com/index.php?title=Appendix:High_Valyrian_Duolingo_Tips_and_Notes#Future',
    'https://wiki.languageinvention.com/index.php?title=Appendix:High_Valyrian_Duolingo_Tips_and_Notes#Present_tense',
    'https://wiki.languageinvention.com/index.php?title=High_Valyrian_Grammar#Future',
    'https://wiki.languageinvention.com/index.php?title=User:Najahho/High_Valyrian_Verb_Moods#Uses_of_the_subjunctive',
  ],
  // the week (docs/COURSE.md "Lesson hub")
  grammar: ['future'],
  grammarAlso: ['subjunctive'],
  drills: { tense: ['future'] },
  showLines: ['hotd-s3e03-011', 'hotd-s1e01-021', 'got-s5e03-011'],
  pronunciation: { focus: 'j', words: ['jēngār', 'jēnqēpsa', 'jēnqa', 'naejītsos', 'soljanna'], lines: ['got-s5e03-011'] },
  letter: {
    fi: 'Kerro, mitä teet ensi viikolla ja Halloweenina (futuuri).',
    en: 'Write what you will do next week and at Halloween (future).',
  },
  scene: 'hotd-s1e01-1',
}

# Futuuri

Perfektin jälkeen katse kääntyy toiseen suuntaan. Valyrian futuuri kertoo, mitä tulevaisuudessa tapahtuu – ja mihin puhuja sitoutuu.

## Kaukainen tulevaisuus ja lupaus

Futuurilla ilmaistaan kahta asiaa: **kauempana tulevaisuudessa olevia ennusteita** ja **sitoutumista** johonkin tulevaan. Kun Rhaenyra sanoo:

<Line id="hotd-s3e03-011" />

hän ei kuvaa, mitä on juuri tekemässä, vaan lupaa. Duolingon Tips & Notesin perusesimerkki on tämä:

<Interlinear hv="Daenerys dāri ȳdr|ēlza." gloss="Daenerys-NOM kuningas-ACC puhua-FUT.3SG" fi="Daenerys puhuu kuninkaalle. / Daenerys aikoo puhua kuninkaalle." />

Kun asia tapahtuu aivan pian, valyriassa käytetään **preesensiä**: <Hv glossed>Tubī Daenerys dāri ȳdras</Hv> ’Daenerys puhuu kuninkaalle tänään’.

Tässä suomesta on sekä apua että haittaa. Suomessa ei ole futuurin taivutusmuotoa lainkaan: sanomme *puhun huomenna* preesensillä ja tarvittaessa *aion puhua* tai *tulen puhumaan*. Siksi valyrian preesens lähitulevaisuudesta tuntuu luontevalta. Ansa on muualla: suomalainen on tottunut käyttämään preesensiä *kaikesta* tulevasta, mutta valyriassa kauempana oleva tai luvattu asia vaatii oman muotonsa. Duolingon ohjeiden neuvo on selvä: kun lause viittaa tulevaan, oleta, että siinä käytetään varsinaista futuurimuotoa.

## Indikatiivi: tunnuksena l

Futuurin vartalossa on **l**. Se, mitä juuren ja l:n väliin tulee, riippuu juuren lopusta.

**Konsonanttiin päättyvä juuri** saa väliin <Hv>-il-</Hv>, ja sen perään tulevat preesensin persoonapäätteet. Poikkeus on yksi: yksikön 1. persoonassa äänteenmuutos tuottaa oman päätteensä **<Hv>-inna</Hv>**.

<Paradigm lemma="jorrāelagon" filter="indicative.active.future" numbers="sg,pl" />

<Pronounce word="jorrāelinna" />

Paino on jor·rāe·LIN·na: kaksoiskonsonantti tekee tavusta raskaan.

Huomaa 3. persoonan <Hv>-ilza</Hv> ja <Hv>-ilzi</Hv>. Ne eivät ole uusia: preesensin oppitunnilta muistat, että r:ään ja l:ään päättyvät juuret saavat 3. persoonassa päätteet <Hv>-za</Hv> ja <Hv>-zi</Hv>. Futuurin vartalo päättyy l:ään, joten sama sääntö pätee.

**Vokaaliin päättyvä juuri** saa persoonapäätteet samaan tapaan, mutta l:n edelle tulee **pitkä vokaali**:

- kun juuren vokaali on **a** tai **o**, tulos on <Hv>-ēl-</Hv>
- kun se on **e**, **i** tai **u**, tulos on <Hv>-īl-</Hv>

Duolingon ohjeet näyttävät kaikki viisi tapausta monikon 3. persoonassa:

- <Hv glossed>ȳdragon</Hv> ’puhua’ → <Hv glossed>ȳdrēlzi</Hv> ’he puhuvat’
- <Hv glossed>nektogon</Hv> ’leikata’ → <Hv glossed>nektēlzi</Hv> ’he leikkaavat’
- <Hv glossed>urnegon</Hv> ’nähdä’ → <Hv glossed>urnīlzi</Hv> ’he näkevät’
- <Hv glossed>sindigon</Hv> ’ostaa’ → <Hv glossed>sindīlzi</Hv> ’he ostavat’
- <Hv glossed>bardugon</Hv> ’kirjoittaa’ → <Hv glossed>bardīlzi</Hv> ’he kirjoittavat’

<Paradigm lemma="sindigon" filter="indicative.active.future" numbers="sg,pl" />

<Pronounce word="nektēlzi" />

<Pronounce word="sindīlzi" />

Pitkä vokaali l:n edellä vetää painon puoleensa: nek·TĒL·zi, sin·DĪL·zi.

<Example id="ex-c41ad9ec5a12" />

<Example id="ex-2f08a2eb9b06" />

## Subjunktiivi: uusi u-sarja

Futuurissa tapaat ensimmäistä kertaa valyrian **toisen subjunktiivin päätesarjan**. Se on tuttu sarja, mutta o on vaihtunut u:ksi: <Hv>-un</Hv>, <Hv>-ū</Hv>, <Hv>-us</Hv>, <Hv>-uty</Hv>, <Hv>-ūt</Hv>, <Hv>-usy</Hv>. Päätteet liitetään futuurin vartaloon, joten vokaalivartaloissa pitkä vokaali säilyy: <Hv>nektēlun</Hv>, <Hv>nektēlusy</Hv>.

<Paradigm lemma="jorrāelagon" filter="subjunctive.active.future" numbers="sg,pl" />

<Paradigm lemma="nektogon" filter="subjunctive.active.future" numbers="sg,pl" />

Duolingon ohjeet eivät vielä kerro, mihin futuurin subjunktiivia käytetään, mutta tunnistat muodon jo virallisista lauseista. Kun tuleva teko kielletään sanalla <Hv>daor</Hv>, verbi on subjunktiivissa niin kuin muissakin kieltolauseissa:

<Example id="ex-b9db50ee2817" />

Sama muoto ilmaisee myös toivetta, kuten suomen konditionaali *haluaisin*:

<Example id="ex-26431af6f541" />

Muistatko tämän lauseen <LessonLink n={20}>oppitunnilta 20</LessonLink>? Silloin opettelit sen kokonaisena. Nyt näet, miksi verbi on <Hv l="emagon">emilun</Hv>: se on futuurin subjunktiivi, ja se kääntyy suomeksi *haluaisin*.

<Note>
Sana <Hv glossed>kostilus</Hv> ’ole hyvä, kenties’ näyttää olevan alun perin juuri tällainen muoto: verbin <Hv glossed>kostagon</Hv> ’voida’ futuurin subjunktiivi, kirjaimellisesti suunnilleen ’jos se on mahdollista’. Verbiin palaamme seuraavalla oppitunnilla.
</Note>

## Epäsäännölliset verbit

Duolingon ohjeiden sanoin verbi <Hv glossed>sagon</Hv> ’olla’ on futuurissa *barbaarisen* epäsäännöllinen. Sen futuurin vartalo on **<Hv>kes-</Hv>**, kuten <LessonLink n={3}>oppitunnilla 3</LessonLink> näit, ja se taipuu kuin preesensverbi, mutta merkitys on tuleva:

<Paradigm lemma="sagon" filter="indicative.active.future" numbers="sg,pl" />

Subjunktiivi on yhtä säännöllinen: <Hv l="sagon">keson</Hv>, <Hv>kesō</Hv>, <Hv l="sagon">kesos</Hv>, <Hv>kesoty</Hv>, <Hv>kesōt</Hv>, <Hv l="sagon">kesosy</Hv> – tutut o-päätteet eikä u-sarja.

<Line id="hotd-s1e01-021" />

<Example id="ex-0cb563e580b2" />

Kaksi muuta tuttua verbiä:

- <Hv glossed>emagon</Hv> ’olla jollakin’ on futuurissa **säännöllinen**, konsonanttivartaloinen <Hv>em-</Hv>: <Hv glossed>eminna</Hv> ’minulla tulee olemaan’, <Hv>emilza</Hv>
- <Hv glossed>jagon</Hv> ’mennä’ on lievästi epäsäännöllinen, vartalona <Hv>īl-</Hv>: <Hv glossed>īnna</Hv> ’menen, tulen menemään’

<Example id="ex-d5afeb2c0e99" />

## Kyllä tulevassa ajassa: kessa

Tiedät jo, että valyrian ’kyllä’ on verbin <Hv>sagon</Hv> yksikön 3. persoona <Hv glossed>issa</Hv>, kirjaimellisesti ’on’. Kun se on luontevaa, tämän kyllä-sanan aikamuoto **seuraa lauseen aikamuotoa**. Tulevaan viittaavassa yhteydessä kuulet siis <Hv l="sagon">kessa</Hv>, vaikka <Hv l="sagon">issa</Hv> käy aina.

Suomalaiselle ajatus on tuttu: mekin vastaamme kysymyksiin verbillä. *Tuletko? – Tulen.* *Onko se valmis? – On.* Valyriassa tämä ’on’ vain taipuu aikamuodossa.

<Line id="got-s5e03-011" />

<Example id="ex-e4d918a6dd2b" />

<Pronounce l="kessa" word="kessa" />

## Harjoituslauseita

<Practice hv="Vala ābre urnīlza." fi="Mies tulee näkemään naisen." />

<Practice hv="Havon sindīnna." fi="Ostan leivän. (Aion ostaa leivän.)" />

<Practice hv="Ābra havon nektēlza." fi="Nainen leikkaa leivän (tulevaisuudessa)." />

<Practice hv="Dārys kesan." fi="Minusta tulee kuningas." />

<Practice hv="Kessa, īnna." fi="Kyllä, minä menen." />

## Sanat

<Vocab />

<Check />
